import React, { useEffect, useRef, useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { searchIcon } from "./svgs";
import api from "../../api/api";
import { useSelector } from "react-redux";
import ToastMessage from "../../utils/mui/righttoptoast";
import { getPracticeId } from "../../config/practice";


export default function FlagModal({ open, defaultText = "", onClose = () => {}, onUpdate = () => {} }) {
  const [text, setText] = useState(defaultText);
  const [query, setQuery] = useState("");
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success"); // or "error"
  const [openToast, setOpenToast] = useState(false);

  const searchWrapRef = useRef(null);
  const practiseid = getPracticeId();
  const userConfig = useSelector((state) => state.userconfig);

  const handleToastClose = () => setOpenToast(false);

  useEffect(() => {
    if (!open) return;
    setText(defaultText);

    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const response = await api.post(`/v1/${practiseid}/appointments/dashboard`, {
          appointmentdate: new Date().toISOString().split("T")[0],
          dashfilter: null,
          filterarr: [],
          limit: 0,
          searchflag: null,
          start: 0,
        });
        setDashboardData(response.data || []);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setDashboardData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [open, defaultText, practiseid]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return dashboardData.slice(0, 6);
    return dashboardData
      .filter(
        (p) =>
          (p.firstname + " " + p.lastname).toLowerCase().includes(q) ||
          (p.patientid + "").toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [query, dashboardData]);

  const alreadySelected = (p) => selectedPatients.some((sp) => sp.patientid === p.patientid);
  const addPatient = (p) => {
    if (alreadySelected(p)) return;
    setSelectedPatients((prev) => [...prev, p]);
    setQuery("");
    setShowResults(false);
  };
  const removePatient = (id) => setSelectedPatients((prev) => prev.filter((p) => p.patientid !== id));

  const handleEnterAdd = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length > 0) addPatient(filtered[0]);
    }
  };

  const handleUpdate = async () => {
    if (selectedPatients.length === 0) {
      setToastSeverity("error");
      setToastMessage("Please select at least one patient");
      setOpenToast(true);
      return;
    }

    const nameString = selectedPatients.map((p) => `${p.firstname} ${p.lastname}`).join(", ");

    const payload = {
      name: nameString,
      priority: 1,
      email: userConfig?.email || "",
      status: 0,
      subject: text,
      message: text,
      userid: 0,
    };

    try {
      const response = await api.post(`/v1/${practiseid}/support/create-ticket`, payload);
      console.log("Ticket created:", response.data);
      setToastSeverity("success");
      setToastMessage("Ticket created successfully!");
      setOpenToast(true);
     setTimeout(() => {
      setOpenToast(false);
      onUpdate(payload);
      onClose();
    }, 1200);
    } catch (err) {
      console.error("Error creating ticket:", err);
      setToastSeverity("error");
      setToastMessage("Failed to create ticket.");
      setOpenToast(true);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="flag-title"
          className="relative w-full max-w-[512px] bg-white rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 mt-2 border-b">
            <h3 id="flag-title" className="text-[17px] font-medium text-[#1F2937]">
              What's wrong?
            </h3>
            <button onClick={onClose} aria-label="Close" className="p-1 focus:outline-none">
              <FontAwesomeIcon icon={faXmark} className="text-[#9CA3AF] text-[18px]" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {/* Search */}
            <div className="mb-4" ref={searchWrapRef}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2">{searchIcon}</span>
                <input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowResults(true);
                  }}
                  onFocus={() => setShowResults(true)}
                  onKeyDown={handleEnterAdd}
                  placeholder={loading ? "Loading patients..." : "Search patients..."}
                  disabled={loading}
                  className="w-full rounded-lg border border-[#D1D5DB] pl-10 pr-3 font-inter font-normal tracking-wide py-2 text-[14px] placeholder:text-[#CCCCCC] outline-none"
                />
                {showResults && !loading && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-64 overflow-auto">
                    {filtered.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
                    ) : (
                      filtered.map((p) => (
                        <button
                          key={p.patientid}
                          type="button"
                          onClick={() => addPatient(p)}
                          disabled={alreadySelected(p)}
                          className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#F9FAFB] transition ${
                            alreadySelected(p) ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        >
                          <span className="text-[14px] text-[#111827]">
                            {p.firstname} {p.lastname}
                            <span className="ml-2 text-xs text-[#6B7280]">• {p.patientid}</span>
                          </span>
                          {!alreadySelected(p) && <span className="text-[12px] text-[#2563EB]">Add</span>}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Selected Chips */}
              {selectedPatients.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {selectedPatients.map((p) => (
                    <span key={p.patientid} className="inline-flex items-center gap-2 bg-[#DBEAFE] text-[#1E40AF] px-2 py-1 rounded-md text-[12px]">
                      {p.firstname} {p.lastname}
                      <button
                        type="button"
                        onClick={() => removePatient(p.patientid)}
                        className="cursor-pointer leading-none -mr-1"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Textarea */}
            <div className="rounded-xl border-[1px] border-[#E5E7EB]">
              <textarea
                rows={6}
                placeholder="Enter issue here..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="w-full resize-none rounded-xl px-4 py-3 outline-none placeholder:text-[#CCCCCC] text-[14px]"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-[#F9FAFB] flex justify-end">
            <button
              onClick={handleUpdate}
              className="inline-flex items-center px-5 py-2.5 rounded-xl bg-[#2563EB] text-white text-[14px] font-medium"
            >
              Update
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      <ToastMessage
        message={toastMessage}
        severity={toastSeverity}
        open={openToast}
        handleClose={handleToastClose}
      />
    </>
  );
}
